import React, { useState, useEffect } from 'react';
import { Home, UserCheck, Smartphone, User, Briefcase } from 'lucide-react';

const STORAGE_KEY = 'doorguard_feature_view';

const CLIENT_CARDS = [
  {
    Icon: Home,
    title: 'No gym required.',
    body: 'The coach comes to you. Book a session at your house, your garage, a field, or wherever you want to train.',
  },
  {
    Icon: UserCheck,
    title: 'Independent Coaches.',
    body: 'Real coaches. Not gym floor employees.',
  },
  {
    Icon: Smartphone,
    title: 'Uber-style booking.',
    body: 'Find a coach near you, pick a time, pick a place and enjoy the process!',
  },
];

const COACH_COPY = "You stop doing the work that has nothing to do with coaching. No more cold outreach, no more word of mouth grinding, no more posting online hoping the right person sees it. Clients come to you through the platform, you set your own rates per service, and you get paid fast directly to your bank account. Your reputation compounds on the platform through reviews. Every good session builds something that belongs to you.";

const FeatureToggle = () => {
  const [view, setView] = useState('client');

  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === 'coach' || saved === 'client') setView(saved);
    } catch { /* ignore */ }
  }, []);

  const switchTo = (v) => {
    setView(v);
    try { localStorage.setItem(STORAGE_KEY, v); } catch { /* ignore */ }
  };

  return (
    <section
      className="py-24 bg-gradient-to-b from-[#09090B] to-[#0F0F11]"
      data-testid="feature-toggle-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-8" data-testid="why-doorguard-title">
          Why DojoDash
        </h2>
        {/* Toggle */}
        <div className="flex justify-center mb-10">
          <div className="inline-flex rounded-full border border-zinc-800 bg-[#18181B] p-1" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'client'}
              onClick={() => switchTo('client')}
              className={`flex items-center gap-2 px-5 py-2 text-sm rounded-full transition-colors ${
                view === 'client' ? 'bg-[#C0A062] text-black font-semibold' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="toggle-client-view"
            >
              <User className="w-4 h-4" />
              I&apos;m a Client
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'coach'}
              onClick={() => switchTo('coach')}
              className={`flex items-center gap-2 px-5 py-2 text-sm rounded-full transition-colors ${
                view === 'coach' ? 'bg-[#C0A062] text-black font-semibold' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid="toggle-coach-view"
            >
              <Briefcase className="w-4 h-4" />
              I&apos;m a Coach
            </button>
          </div>
        </div>

        {view === 'client' ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="client-cards">
            {CLIENT_CARDS.map(({ Icon, title, body }) => (
              <div
                key={title}
                className="bg-[#18181B] border border-zinc-800 rounded-lg p-6 hover:border-[#C0A062]/40 transition-colors"
                data-testid={`client-card-${title.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
              >
                <div className="w-10 h-10 rounded-md bg-[#C0A062]/10 border border-[#C0A062]/40 flex items-center justify-center mb-4">
                  <Icon className="w-5 h-5 text-[#C0A062]" />
                </div>
                <p className="text-white font-semibold mb-2">{title}</p>
                <p className="text-sm text-zinc-400 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        ) : (
          <div className="max-w-3xl mx-auto" data-testid="coach-card">
            <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-8 sm:p-10 hover:border-[#C0A062]/40 transition-colors">
              <div className="w-12 h-12 rounded-md bg-[#C0A062]/10 border border-[#C0A062]/40 flex items-center justify-center mb-6">
                <Briefcase className="w-6 h-6 text-[#C0A062]" />
              </div>
              <p className="text-zinc-300 text-base sm:text-lg leading-relaxed">{COACH_COPY}</p>
              <button
                type="button"
                onClick={() => switchTo('client')}
                className="mt-6 text-sm text-[#C0A062] hover:text-[#a98a52] inline-flex items-center gap-1"
                data-testid="switch-to-client"
              >
                ← Switch to Client View
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FeatureToggle;
